import React, { createContext, useCallback, useContext, useEffect, useState } from "react";
import { useDomViewport } from "web/react/hooks/use-dom-viewport/use-dom-viewport";
import analytics from "web/script/analytics/analytics";

export const NAV_ANALYTICS_CATEGORY = "top_nav";

export type MenuCategoryType =
    | "product_type"
    | "product_category"
    | "featured_category"
    | "guides"
    | "the_lyst_index"
    | "shopfronts";

type ViewType = "desktop" | "mobile" | null;

type ActionType = "clicked" | "impression" | "opened" | "closed";
interface NavigationContextProps {
    showMenu: ViewType;
    setShowMenu: React.Dispatch<React.SetStateAction<ViewType>>;
    showSearch: ViewType;
    setShowSearch: React.Dispatch<React.SetStateAction<ViewType>>;
    handleMenuOpenedAnalytics: (
        menuState: Omit<ActionType, "clicked" | "impression">,
        targetGender: string
    ) => void;
    handleCategoryAnalytics: (
        label: MenuCategoryType | "shopfronts_view_all",
        action?: ActionType,
        customData?: Record<string, unknown>
    ) => void;
    handleHeaderAnalytics: (buttonName: string, action?: ActionType) => void;
    handleNavigationAnalytics: (navButtonName: string) => void;
    hideGenderControl: boolean;
    setHideGenderControl: React.Dispatch<React.SetStateAction<boolean>>;
}
interface NavigationContextProviderProps {
    children: React.ReactNode;
}

const NavigationContext = createContext<NavigationContextProps>({
    showMenu: null,
    setShowMenu: () => {},
    showSearch: null,
    setShowSearch: () => {},
    handleMenuOpenedAnalytics: () => {},
    handleNavigationAnalytics: () => {},
    handleHeaderAnalytics: () => {},
    handleCategoryAnalytics: () => {},
    hideGenderControl: false,
    setHideGenderControl: () => {},
});

export function NavigationContextProvider({
    children,
}: NavigationContextProviderProps): React.ReactElement {
    const [showMenu, setShowMenu] = useState<ViewType>(null);
    const [showSearch, setShowSearch] = useState<ViewType>(null);
    const [hideGenderControl, setHideGenderControl] = useState(false);

    const { isDesktopViewport } = useDomViewport();
    const handleMenuOpenedAnalytics = useCallback(function (menuState, targetGender): void {
        analytics.event("top_nav", menuState, `nav_${targetGender.toLowerCase()}_menu`);
    }, []);

    const handleCategoryAnalytics = useCallback(function (
        label: MenuCategoryType | "shopfronts_view_all",
        action: ActionType = "clicked",
        customData?: Record<string, unknown>
    ): void {
        analytics.event({
            category: NAV_ANALYTICS_CATEGORY,
            action,
            label,
            ...(customData ? { customData } : {}),
            truncate: false,
        });
    },
    []);

    const handleNavigationAnalytics = useCallback(function (navButtonName): void {
        analytics.event("top_nav", "clicked", navButtonName);
    }, []);

    const handleHeaderAnalytics = useCallback(function (buttonName, action = "clicked"): void {
        analytics.event("top_nav", action, buttonName);
    }, []);
    /** Auto hides mobile search if viewport changes to desktop*/
    useEffect(() => {
        if (isDesktopViewport) {
            setShowSearch(null);
        }
    }, [isDesktopViewport]);

    const value = {
        showMenu,
        setShowMenu,
        showSearch,
        setShowSearch,
        handleMenuOpenedAnalytics,
        handleCategoryAnalytics,
        handleNavigationAnalytics,
        handleHeaderAnalytics,
        hideGenderControl,
        setHideGenderControl,
    };
    return <NavigationContext.Provider value={value}>{children}</NavigationContext.Provider>;
}

export function useNavigationContext(): NavigationContextProps {
    return useContext(NavigationContext);
}
